import { DocumentReference } from "firebase/firestore";

import { DataHelpers } from "@/core/modules/helpers/DataHelpers";
import { FirestoreDocument } from "@/core/modules/firestore/objects/FirestoreDocument";
import { LinkedCompany } from "@/features/modules/company/objects/LinkedCompany";
import { LinkedParcel } from "@/features/modules/parcel/objects/LinkedParcel";
import { LinkedTransportType } from "@/features/modules/transportType/objects/LinkedTransportType";
import { ParcelData } from "@/features/modules/parcel/objects/ParcelData";

import { ArrayByKeyField, ArrayField, DateStrictField, ObjectField, StringArrayField, StringField } from "@/core/fields";

export class InboundShipment extends FirestoreDocument {
  public date: Date = new Date();
  public company: LinkedCompany | undefined = undefined;
  public code: string | undefined = undefined;
  public parcelsData: ParcelData[] = [];
  public parcels: Record<string, LinkedParcel> = {};
  public parcelTypesIds: string[] = [];
  public detail: string | undefined = undefined;
  public transportType: LinkedTransportType | undefined;

  public constructor(firestoreData?: Record<string, unknown>, id?: string) {
    super(id);
    if (firestoreData !== undefined) this.fromFirestore(firestoreData, id);
  }

  public fromFirestore(data: Record<string, unknown>, id?: string, firestoreRef?: DocumentReference): InboundShipment {
    super.fromFirestore(data, id, firestoreRef);

    this.date = DateStrictField.fromFirestore(data.date, new Date());
    this.company = ObjectField.fromFirestore<LinkedCompany>(data.company, (value) => new LinkedCompany(value));
    this.code = StringField.fromFirestore(data.code);
    this.parcelsData = ArrayField.fromFirestore<ParcelData>(data.parcelsData, (value) => new ParcelData(value));
    this.parcels = ArrayByKeyField.fromFirestore<LinkedParcel>(data.parcels, (value) => new LinkedParcel(value));
    this.parcelTypesIds = StringArrayField.fromFirestore(data.parcelTypesIds);
    this.detail = StringField.fromFirestore(data.detail);
    this.transportType = ObjectField.fromFirestore<LinkedTransportType>(data.transportType, (value) => new LinkedTransportType(value));

    return this;
  }

  public toFirestore(): Record<string, unknown> {
    const firestoreData: Record<string, unknown> = super.toFirestore();

    firestoreData.date = DateStrictField.toFirestore(this.date);
    firestoreData.company = ObjectField.toFirestore<LinkedCompany>(this.company, (value) => value.toFirestore());
    firestoreData.code = StringField.toFirestore(this.code);
    firestoreData.parcelsData = ArrayField.toFirestore<ParcelData>(this.parcelsData, (value) => value.toFirestore());
    firestoreData.parcels = ArrayByKeyField.toFirestore<LinkedParcel>(this.parcels, (value) => value.toFirestore());
    firestoreData.parcelTypesIds = StringArrayField.toFirestore(this.parcelTypesIds);
    firestoreData.detail = StringField.toFirestore(this.detail);
    firestoreData.transportType = ObjectField.toFirestore<LinkedTransportType>(this.transportType, (value) => value.toFirestore());

    return firestoreData;
  }

  public setSearchKeys(): void {
    this.searchKeys = [];
    if (this.code !== undefined) this.searchKeys.push(this.code.toLowerCase());
  }

  public getLinkedParcels(): LinkedParcel[] {
    return DataHelpers.objectToSortedArray<LinkedParcel>(this.parcels);
  }

  public setLinkedParcels(linkedParcels: LinkedParcel[]): void {
    this.parcels = DataHelpers.sortedArrayToObject<LinkedParcel>(linkedParcels);
    const parcelTypes: string[] = Object.values(this.parcels).map((linkedParcel) => linkedParcel.type?.id ?? "EMPTY");
    this.parcelTypesIds = parcelTypes.filter(function (linkedParcelTypeId: string, index: number) {
      return linkedParcelTypeId !== "EMPTY" && parcelTypes.indexOf(linkedParcelTypeId) === index;
    });
  }

  public addLinkedParcel(linkedParcel: LinkedParcel): void {
    this.parcels[linkedParcel.id] = linkedParcel;
    const parcelTypes: string[] = Object.values(this.parcels).map((linkedParcel) => linkedParcel.type?.id ?? "EMPTY");
    this.parcelTypesIds = parcelTypes.filter(function (linkedParcelTypeId: string, index: number) {
      return linkedParcelTypeId !== "EMPTY" && parcelTypes.indexOf(linkedParcelTypeId) === index;
    });
  }

  public removeLinkedParcel(linkedParcel: LinkedParcel): void {
    delete this.parcels[linkedParcel.id];
    if (linkedParcel.type === undefined) return;
    const parcelTypes: string[] = Object.values(this.parcels).map((linkedParcel) => linkedParcel.type?.id ?? "EMPTY");
    this.parcelTypesIds = parcelTypes.filter(function (linkedParcelTypeId: string, index: number) {
      return linkedParcelTypeId !== "EMPTY" && parcelTypes.indexOf(linkedParcelTypeId) === index;
    });
  }

  public emptyLinkedParcels(): void {
    this.parcels = {};
    this.parcelTypesIds = [];
  }
}
